import { useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Alert } from 'react-bootstrap';
import TaskGenEditor from '../../config/ckeditor';
import AnalyzedText from './AnalyzedText';
import { Overlay } from './Overlay';
import { useNavigate } from "react-router-dom";
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { handleApiError, safeJsonParse, showErrorNotification } from "../../utils/api";

export interface CustomEditorProps {
    id: string;
    label: string;
    data: string;
    formatted: string;
    change: CallableFunction;
    changeFormatted: CallableFunction;
    npl: boolean;
    task_id: string;
    task_language?: string;
}

export default function CustomEditor(props: CustomEditorProps) {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const [processingText, setProcessingText] = useState(false);
    const [text, setText] = useState(props.data);
    const [messageDuration, setMessageDuration] = useState('');
    const [analyzedText, setAnalyzedText] = useState<any>(null);
    const [formattedText, setFormattedText] = useState(props.formatted);
    const [showAnalyze, setShowAnalyze] = useState(false);

    const handleTextAnalyzed = (data: any) => {
        setShowAnalyze(false);
        setAnalyzedText(data);
        setShowAnalyze(true);
    }

    const processText = (e: any) => {
        e.preventDefault();
        setShowAnalyze(false);
        if (text.length > 0) {
            setProcessingText(true);

            setMessageDuration('');

            const plainText = text.replaceAll('<p>', '').replaceAll('<br>', '\n\n').replaceAll('</p>', '\n\n\n\n').replace(/<[^>]*>?/gm, '');

            let _data = {
                uid: Math.random().toString(36).slice(-5),
                language: props.task_language || 'en',
                text: plainText
            }

            const startTimestamp = Date.now();
            fetch(import.meta.env.VITE_API_URL + '/v1/nlp/run', {
                method: "POST",
                body: JSON.stringify(_data),
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            })
            .then(response => {
                if (handleApiError(response, navigate)) {
                    showErrorNotification(t("We are sorry! There was a processing error. Text Analysis is out of order at this moment. Give us some time to recover and try again later."));
                    setProcessingText(false);
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(json => {
                if (!json) return;
                const execTime = Date.now() - startTimestamp;
                const totalSeconds = Math.floor(execTime / 1000);
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                const messageArray: string[] = [];
                if (hours > 0) {
                    messageArray.push(hours == 1 ? hours + ' ' + t("hour") : hours + ' ' + t("hours"));
                }
                if (minutes > 0) {
                    messageArray.push(minutes == 1 ? minutes + ' ' + t("minute") : minutes + ' ' + t("minutes"));
                }
                if (seconds > 0) {
                    messageArray.push(minutes == 1 ? seconds + ' ' + t("second") : seconds + ' ' + t("seconds"));
                }
                setMessageDuration(messageArray.join(' '));

                // Fix paragraph numbers: the backend often returns all tokens
                // with the same paragraph number. We detect paragraph breaks
                // by looking for large gaps in start_char between consecutive
                // tokens. The </p> → \n\n\n\n replacement creates gaps >= 4.
                if (json.main && json.main.length > 0) {
                    let paragraphNum = 1;
                    let prevEndChar = -1;
                    for (const token of json.main) {
                        if (token.start_char === '-') continue;
                        const sc = parseInt(token.start_char);
                        const ec = parseInt(token.end_char);
                        if (isNaN(sc) || isNaN(ec)) continue;
                        if (prevEndChar >= 0 && (sc - prevEndChar) >= 3) {
                            paragraphNum++;
                        }
                        token.paragraph = String(paragraphNum);
                        prevEndChar = ec;
                    }
                }

                setProcessingText(false);
                handleTextAnalyzed(json);
            }).catch(err => console.log(err));
        }
    }

    const handleSaveFormattedText = (text: string) => {
        setFormattedText(text);
        props.changeFormatted(text);
    }

    return (
        <div className={ 'field field-custom-editor ' + props.id }>
            <div className="label">{ props.label }</div>
            <div className="editor-container">
                <CKEditor
                    editor={ TaskGenEditor }
                    data= { props.data }
                    onChange={ ( event, editor: any ) => {
                        const data = editor.getData();
                        setText(data);
                        props.change(data);
                    } }
                    config = { {
                        toolbar: {
                            items: [ 'heading', 'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor', '|', 
                                'bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', '|',
                                'bulletedList', 'numberedList', 'indent', 'outdent', 'alignment', 'horizontalLine', '|',
                                'link', 'uploadImage', 'mediaEmbed', '|',
                                'insertTable', '|',
                                'PasteFromOffice', 'removeFormat'
                                ],
                            shouldNotGroupWhenFull: true,
                        }
                    }}
                />
            </div>
            { props.formatted && 
                <div className="formatted-text">
                    <div className="label">{ t("Previously formatted text") }</div>
                    <div className="value">{ parse(props.formatted) }</div>
                </div>
            }
            { props.npl && text.length > 0 &&
                <div className="text-processing-container">
                    <Alert variant='warning'>
                        <Alert.Heading><strong>{ t("Important") }</strong></Alert.Heading>
                        <div>
                            { t("The tool is still under construction, but give it a try! We\'re working on it.") }
                        </div>
                    </Alert>
                    <Alert variant='info'>
                        <div>
                            <strong>{ t("Analysis language") }:</strong> { t(props.task_language === 'ca' ? 'Catalan' : props.task_language === 'en' ? 'English' : props.task_language === 'es' ? 'Spanish' : props.task_language === 'ru' ? 'Russian' : 'English') }
                        </div>
                        <small className="text-muted">
                            { t("To change the analysis language, modify the task language in the task metadata section") }
                        </small>
                    </Alert>
                    <button className={ 'btn btn-primary process-text' + (processingText ? ' processing' : '') }
                        onClick={ processText }>
                        { t("Analyze the text") }
                    </button>
                    { messageDuration &&
                        <span>{ t("Text analyzed in") } <strong>{ messageDuration }</strong></span>
                    }
                </div>
            }
            { showAnalyze &&
                <Overlay
                    title={ t("Text analysis") }
                    onClose={ () => setShowAnalyze(false) }
                    className="text-analyzer-overlay"
                    actions={[{ label: t("Done"), variant: 'primary', onClick: () => setShowAnalyze(false) }]}
                >
                    <AnalyzedText task_id={ props.task_id } analyzedText={ analyzedText } saveFormattedTextCallback={ handleSaveFormattedText } language={ props.task_language || 'en' } />
                </Overlay>
            }
        </div>
    );
}